import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Grid, Box, FormControl, FormLabel, FormGroup, FilledInput, Tooltip, IconButton, Icon, Select, MenuItem } from '@material-ui/core';
import { DataGrid } from '@material-ui/data-grid';
import { useLocation, Link } from 'react-router-dom';

import Loader from '../../common/loader';
import Crud from '../../common/crud';
import NotFound from '../../common/not-found';
import ViewTitle from '../../common/view-title';
import Trash from '../../common/trash';

import AuthContext from '../../context';
import { del, get, post, put } from '../../utils/api-services';
import helpers from '../../utils/helpers';

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        paddingBottom: 20
    },
    tableContainer: {
        width: '100%'
    }
}));

const CostCenter = () => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const classes = useStyles();
    const { pathname } = useLocation();
    const [state, setState] = useState({
        loading: true,
        data: [],
        cc: {
            idcost_center: 0,
            code: '',
            name: '',
            selected: false
        },
        permissions: {}
    });

    useEffect(() => {
        window.document.title = 'Cost center';

        validatePermissions();
    }, []);

    const validatePermissions = () => {
        const permissions = helpers.getPermissions(pathname);
        setState(prev => ({ ...prev, permissions }));

        (permissions.read_attr) ? makeRequest() : setState(prev => ({ ...prev, loading: false }));
    }

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/costCenter');
        if (response.status === 200) {
            setState(prev => ({ ...prev, loading: false, data: response?.data ?? [] }));
        } else {
            console.log('[CostCenter] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const handleChange = ({ target }) => setState(prev => ({ ...prev, cc: { ...prev.cc, [target.name]: target.value } }));

    const validateForm = (action) => {
        const { idcost_center, code, name } = state.cc;

        switch (action) {
            case 'update':
                const data = state.data.filter(value => value.idcost_center === idcost_center);

                if (data[0]) {
                    const before = JSON.stringify(data[0]);
                    const after = JSON.stringify(state.cc);
                    if (before !== after) {
                        if (code.trim() && name.trim()) {
                            return true;
                        } else {
                            showToast('All fields are required', 'error');
                            return false;
                        }
                    } else {
                        showToast('Update some field', 'error');
                        return false;
                    }
                } else {
                    showToast('Select some cost center', 'error');
                    return false;
                }

            case 'store':

                if (code.trim() && name.trim()) {
                    return true;
                } else {
                    showToast('All fields are required', 'error');
                    return false;
                }

            case 'delete':
                if (idcost_center === 0) {
                    showToast('Select some cost center', 'error');
                    return false;
                }
                const cond = window.confirm('Are you sure you want to delete this cost center?');

                return Boolean(idcost_center && cond);
            default:
                break;
        }
    }

    const clear = () => setState(prev => ({
        ...prev, cc: {
            ...prev.cc,
            idcost_center: 0,
            code: '',
            name: '',
            selected: false
        }
    }));


    const store = async () => {
        const cond = validateForm('store');

        if (cond) {
            showLoader(true);
            const response = await post('/costCenter/store', state.cc);
            if (response.status === 200) {
                setState(prev => ({ ...prev, data: response?.data ?? prev.data }));
                showToast(response.message);
            } else {
                console.log('[CostCenter] store error', response);
                helpers.failRequest(response, showToast, signOut);
                showLoader(false);

                return false
            }
            showLoader(false);
        }

        return cond;
    }

    const update = async () => {
        const { idcost_center } = state.cc;
        const cond = validateForm('update');

        if (cond) {
            showLoader(true);
            const response = await put(`/costCenter/${idcost_center}/update`, state.cc);
            if (response.status === 200) {
                setState(prev => ({ ...prev, data: response?.data ?? prev.data }));
                showToast(response.message);
            } else {
                console.log('[CostCenter] update error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const destroy = async () => {
        const { idcost_center } = state.cc;
        const cond = validateForm('delete');

        if (cond) {
            showLoader(true);
            const response = await del(`/costCenter/${idcost_center}`);
            if (response.status === 200) {
                setState(prev => ({ ...prev, data: response?.data ?? prev.data }));
                showToast(response.message);
            } else {
                console.log('[CostCenter] destroy error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const valueGetter = ({ row, field }) => (row?.sedes[0] === undefined) ? 'N/A' : (row.sedes[0][field]) ? '✓' : '✗';

    const renderCell = ({ row }) => {
        return (
            <Link to={`/history/cost_center/${row.idcost_center}`} target='_blank'>
                <Tooltip title='Show history' placement='top'>
                    <IconButton>
                        <Icon>{'history'}</Icon>
                    </IconButton>
                </Tooltip>
            </Link>
        );
    }

    const columns = [
        { field: 'code', headerName: 'Code', flex: 1 },
        { field: 'name', headerName: 'Name', flex: 1 },
        { field: 'selected', headerName: 'Selected (Hours API)', flex: 1, valueGetter },
        { field: 'actions', headerName: 'Actions', flex: 0.3, renderCell }
    ];

    if (state.loading) {
        return <Loader />;
    }
    if (!state.permissions.read_attr) {
        return <NotFound />;
    }

    return (
        <div className={classes.root}>
            <ViewTitle title='Cost center' />
            <div>
                <Grid container>
                    <Grid item xs={12} md={4}>
                        <FormGroup>
                            <FormLabel>{'Code'}</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <FilledInput
                                    autoComplete='off'
                                    type='text'
                                    placeholder='Code'
                                    name='code'
                                    value={state.cc.code}
                                    onChange={handleChange}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormGroup>
                            <FormLabel>{'Name'}</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <FilledInput
                                    autoComplete='off'
                                    type='text'
                                    placeholder='Name'
                                    name='name'
                                    value={state.cc.name}
                                    onChange={handleChange}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormGroup>
                            <FormLabel>{'Selected (Hours API)'}</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <Select
                                    value={state.cc.selected}
                                    name='selected'
                                    onChange={handleChange}
                                >
                                    <MenuItem value={false}>{'No'}</MenuItem>
                                    <MenuItem value={true}>{'Yes'}</MenuItem>
                                </Select>
                            </FormControl>
                        </FormGroup>
                    </Grid>
                </Grid>
            </div>

            <Crud
                permissions={state.permissions}
                create={{ create: clear, store }}
                update={update}
                destroy={destroy}
                cancel={clear}
            />

            {
                state.permissions.delete_attr &&
                <Trash table='cost_center' id='idcost_center' onRefresh={makeRequest} />
            }

            <div className={classes.tableContainer}>
                <DataGrid
                    rows={state.data}
                    columns={columns}
                    pageSize={45}
                    density='compact'
                    autoHeight
                    getRowId={row => row.idcost_center}
                    onRowClick={({ row }) => {
                        const selected = row?.sedes[0]?.selected ?? false;

                        setState(prev => ({ ...prev, cc: { ...row, selected } }));
                    }}
                />
            </div>
        </div>
    );
}

export default CostCenter;